import { useLingui } from '@lingui/react/macro';

import { Checkbox } from '@/shared/ui/checkbox';
import { createContext, use, type ReactNode } from 'react';
import { useController, useFormContext, useWatch, type FieldValues, type Path, type PathValue } from 'react-hook-form';

import { getFeatureFlag, useConfig } from '@/shared/config';
import { documentInputRaw, maskDocumentInput } from '@/shared/format/document';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Combobox, type ComboboxOption } from '@/shared/ui/combobox';

import { DateField } from '@/shared/ui/date-field';
import { FormField, type FieldControlProps } from '@/shared/ui/field';
import { Input } from '@/shared/ui/input';
import { MaskedField, type MaskKind } from '@/shared/ui/masked-field';
import { MoneyField } from '@/shared/ui/money-field';
import { PercentField } from '@/shared/ui/percent-field';
import { Select, type SelectOption } from '@/shared/ui/select';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';

import { useAgentFillMark } from './agent-fill-ui';

interface FormScreenState {
  readonly readOnly: boolean;
}

const FormStateContext = createContext<FormScreenState>({ readOnly: false });

/** Somente leitura para todos os campos ligados abaixo (o `FormScreen` injeta). */
export function FormStateProvider({
  readOnly,
  children,
}: {
  readonly readOnly: boolean;
  readonly children: ReactNode;
}) {
  return <FormStateContext value={{ readOnly }}>{children}</FormStateContext>;
}

export interface BoundFieldProps<V extends FieldValues> {
  readonly name: Path<V>;
  readonly label: ReactNode;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly hint?: ReactNode;
  readonly className?: string;
}

/**
 * Liga um controle de `shared/ui` ao react-hook-form do formulário (U09): valor, `onChange`,
 * `onBlur`, `ref` (foco no primeiro erro), erro do zod ou da API por campo, e somente leitura.
 */
export function useBound<V extends FieldValues>({
  name,
  readOnly,
}: Pick<BoundFieldProps<V>, 'name' | 'readOnly'>) {
  const { control } = useFormContext<V>();
  const state = use(FormStateContext);
  const { field, fieldState } = useController({ name, control });
  return { field, error: fieldState.error?.message, readOnly: readOnly === true || state.readOnly };
}

/** Moldura de um campo ligado (rótulo, erro, somente leitura) para os campos compostos de `shared/form`. */
export function Bound<V extends FieldValues>({
  props,
  orientation,
  children,
}: {
  readonly props: BoundFieldProps<V>;
  readonly orientation?: 'stacked' | 'inline';
  readonly children: (
    control: FieldControlProps,
    bound: ReturnType<typeof useBound<V>>,
  ) => ReactNode;
}) {
  const bound = useBound<V>(props);
  const mark = useAgentFillMark(props.name);
  return (
    <FormField
      name={props.name}
      adornment={mark}
      label={props.label}
      required={props.required === true}
      disabled={props.disabled === true}
      readOnly={bound.readOnly}
      hint={props.hint}
      error={bound.error}
      {...(orientation ? { orientation } : {})}
      {...(props.className ? { className: props.className } : {})}
    >
      {(control) => children(control, bound)}
    </FormField>
  );
}

/** Texto livre (também e-mail, `type="email"`). */
export function TextFormField<V extends FieldValues>(
  props: BoundFieldProps<V> & {
    readonly type?: 'text' | 'email' | 'tel' | 'password' | 'number';
    readonly placeholder?: string;
    readonly autoComplete?: string;
    /** Teclado do celular (U26): `numeric` para número, agência e conta. O valor continua texto. */
    readonly inputMode?: 'text' | 'numeric' | 'decimal' | 'email' | 'tel';
    readonly maxLength?: number;
  },
) {
  return (
    <Bound props={props}>
      {(control, { field }) => (
        <Input
          {...control}
          ref={field.ref}
          type={props.type ?? 'text'}
          value={
            typeof field.value === 'string'
              ? field.value
              : typeof field.value === 'number'
                ? String(field.value)
                : ''
          }
          onChange={(event) => field.onChange(event.target.value)}
          onBlur={field.onBlur}
          {...(props.placeholder ? { placeholder: props.placeholder } : {})}
          {...(props.autoComplete ? { autoComplete: props.autoComplete } : {})}
          {...(props.type === 'email' ? { inputMode: 'email' as const } : {})}
          {...(props.inputMode ? { inputMode: props.inputMode } : {})}
          {...(props.maxLength === undefined ? {} : { maxLength: props.maxLength })}
        />
      )}
    </Bound>
  );
}

export function TextareaFormField<V extends FieldValues>(
  props: BoundFieldProps<V> & { readonly rows?: number },
) {
  return (
    <Bound props={props}>
      {(control, { field }) => (
        <Textarea
          {...control}
          ref={field.ref}
          rows={props.rows ?? 3}
          value={typeof field.value === 'string' ? field.value : ''}
          onChange={(event) => field.onChange(event.target.value)}
          onBlur={field.onBlur}
        />
      )}
    </Bound>
  );
}

/** CPF, CNPJ, CEP ou telefone: grava o valor cru (U37-8); a máscara do fio é decisão do mapper (U21). */
export function MaskedFormField<V extends FieldValues>(
  props: BoundFieldProps<V> & { readonly kind: MaskKind },
) {
  return (
    <Bound props={props}>
      {(control, { field }) => (
        <MaskedField
          {...control}
          kind={props.kind}
          value={typeof field.value === 'string' ? field.value : ''}
          onChange={(raw) => field.onChange(raw)}
          onBlur={field.onBlur}
        />
      )}
    </Bound>
  );
}

/** Dinheiro em centavos inteiros (U37-8). */
export function MoneyFormField<V extends FieldValues>(props: BoundFieldProps<V>) {
  return (
    <Bound props={props}>
      {(control, { field }) => (
        <MoneyField
          {...control}
          value={typeof field.value === 'number' ? field.value : null}
          onChange={(cents) => field.onChange(cents)}
          onBlur={field.onBlur}
        />
      )}
    </Bound>
  );
}

/** Percentual guardado como fração em string decimal (`'0.0185'` = 1,85%), como o `PercentageFormField` do legado. */
export function PercentFormField<V extends FieldValues>(
  props: BoundFieldProps<V> & {
    readonly decimalScale?: number;
    /** Sentinela ao apagar: `''` permite ao mapper distinguir omissão de null recebido. */
    readonly emptyValue?: '' | null;
  },
) {
  return (
    <Bound props={props}>
      {(control, { field }) => (
        <PercentField
          {...control}
          value={
            typeof field.value === 'string' && field.value !== ''
              ? field.value
              : typeof field.value === 'number'
                ? String(field.value)
                : null
          }
          onChange={(fraction) => field.onChange(fraction ?? props.emptyValue ?? null)}
          onBlur={field.onBlur}
          {...(props.decimalScale === undefined ? {} : { decimalScale: props.decimalScale })}
        />
      )}
    </Bound>
  );
}

/** Data civil ISO (`2026-08-14`), sem fuso (U14). */
export function DateFormField<V extends FieldValues>(props: BoundFieldProps<V> & {
  readonly min?: string;
  readonly max?: string;
  readonly onValueChange?: (value: string | null) => void;
}) {
  return (
    <Bound props={props}>
      {(control, { field }) => (
        <DateField
          {...control}
          value={typeof field.value === 'string' ? field.value : null}
          onChange={(value) => { field.onChange(value); props.onValueChange?.(value); }}
          {...(props.min === undefined ? {} : { min: props.min })}
          {...(props.max === undefined ? {} : { max: props.max })}
          onBlur={field.onBlur}
        />
      )}
    </Bound>
  );
}

/** Lista fechada; casa a opção pelo valor, nunca pelo rótulo (05 §2.2). */
export function SelectFormField<V extends FieldValues>(
  props: BoundFieldProps<V> & {
    readonly options: readonly SelectOption[];
    readonly placeholder?: string;
    /** Depois de gravar o valor: o legado grava também `<campo>Display` e limpa dependentes. */
    readonly onValueChange?: (value: string, option: SelectOption | undefined) => void;
    /** Mostra o "x" do legado (`showButtonClearValue`); limpar grava `clearValue` (padrão `null`). */
    readonly clearable?: boolean;
    readonly clearValue?: '' | null;
    /** Depois de limpar (o legado também limpa o `*Display` e os dependentes). */
    readonly onClear?: () => void;
  },
) {
  const { t } = useLingui();
  return (
    <Bound props={props}>
      {(control, { field }) => (
        <Select
          id={control.id}
          name={control.name}
          required={control.required}
          disabled={control.disabled}
          readOnly={control.readOnly}
          aria-labelledby={control['aria-labelledby']}
          {...(control['aria-describedby']
            ? { 'aria-describedby': control['aria-describedby'] }
            : {})}
          {...(control['aria-invalid'] ? { 'aria-invalid': true } : {})}
          options={props.options}
          value={typeof field.value === 'string' ? field.value : null}
          onChange={(value) => {
            // O <select> nativo do Radix emite '' quando o valor chega depois das opções (registro
            // carregado): nenhum item tem valor vazio, então '' nunca é escolha de quem usa.
            if (value === '') return;
            field.onChange(value);
            field.onBlur();
            props.onValueChange?.(
              value,
              props.options.find((option) => option.value === value),
            );
          }}
          {...(props.placeholder ? { placeholder: props.placeholder } : {})}
          {...(props.clearable
            ? {
                clearLabel: typeof props.label === 'string' ? t`Limpar ${props.label}` : t`Limpar seleção`,
                onClear: () => {
                  field.onChange(props.clearValue === undefined ? null : props.clearValue);
                  field.onBlur();
                  props.onClear?.();
                },
              }
            : {})}
        />
      )}
    </Bound>
  );
}

/** Liga/desliga (booleano). */
export function SwitchFormField<V extends FieldValues>(props: BoundFieldProps<V>) {
  return (
    <Bound props={props} orientation="inline">
      {(control, { field }) => (
        <Switch
          {...control}
          ref={field.ref}
          checked={field.value === true}
          onCheckedChange={(checked) => field.onChange(checked)}
          onBlur={field.onBlur}
        />
      )}
    </Bound>
  );
}

/** Checkbox booleano ligado ao RHF, com rótulo, validação e somente leitura. */
export function CheckboxFormField<V extends FieldValues>(props: BoundFieldProps<V>) {
  return (
    <Bound props={props} orientation="inline">
      {(control, { field }) => (
        <Checkbox
          {...control}
          ref={field.ref}
          checked={field.value === true}
          onCheckedChange={(checked) => field.onChange(checked)}
          onBlur={field.onBlur}
        />
      )}
    </Bound>
  );
}

/**
 * CPF/CNPJ com máscara progressiva (`getDocumentMask` do legado): grava o valor cru (U37-8), CPF até 11
 * caracteres e CNPJ acima disso ou com letra. Com `CNPJ_ALPHANUMERIC` (padrão: a flag da config), aceita
 * letras em maiúsculas; sem ela, só dígitos. A máscara do payload é do mapper (U21).
 */
export function DocumentFormField<V extends FieldValues>(
  props: BoundFieldProps<V> & {
    /** Força a regra do CNPJ alfanumérico; ausente, segue `FEATURE_FLAGS.CNPJ_ALPHANUMERIC`. */
    readonly alphanumeric?: boolean;
    /** Campo só de pessoa jurídica: máscara de CNPJ desde o primeiro caractere. */
    readonly legalPerson?: boolean;
    readonly placeholder?: string;
  },
) {
  const { t } = useLingui();
  const config = useConfig();
  const alphanumeric = props.alphanumeric ?? Boolean(getFeatureFlag(config, 'CNPJ_ALPHANUMERIC'));
  const legalPerson = props.legalPerson === true;
  return (
    <Bound props={props}>
      {(control, { field }) => (
        <Input
          {...control}
          ref={field.ref}
          className="font-mono"
          inputMode={alphanumeric ? 'text' : 'numeric'}
          autoCapitalize={alphanumeric ? 'characters' : 'off'}
          autoComplete="off"
          spellCheck={false}
          maxLength={18}
          // O valor pode chegar formatado (API, padrão): limpa antes da máscara, como o `getDocumentMask`.
          value={maskDocumentInput(documentInputRaw(typeof field.value === 'string' ? field.value : '', { alphanumeric }), { legalPerson })}
          onChange={(event) => field.onChange(documentInputRaw(event.target.value, { alphanumeric }))}
          onBlur={field.onBlur}
          placeholder={
            props.placeholder ??
            (legalPerson ? (alphanumeric ? t`AA.AAA.AAA/0001-AA` : t`00.000.000/0000-00`) : t`000.000.000-00 ou 00.000.000/0000-00`)
          }
        />
      )}
    </Bound>
  );
}

/**
 * Escolha com busca numa lista local, ligada ao RHF (o autocomplete do legado sobre dados já
 * carregados): grava o valor e, com `displayName`, o `*Display` do rótulo. Carregando, erro com
 * "Tentar de novo" e lista vazia têm texto próprio.
 */
export function ComboboxFormField<V extends FieldValues>(
  props: BoundFieldProps<V> & {
    readonly options: readonly ComboboxOption[];
    /** Campo que guarda o rótulo escolhido (`cosifBankAccountCodeDisplay`). */
    readonly displayName?: Path<V>;
    /** Valor gravado ao limpar; padrão `''`, como os formulários de string do legado. */
    readonly emptyValue?: '' | null;
    readonly loading?: boolean;
    /** A lista não carregou: mostra o aviso e o "Tentar de novo". */
    readonly failed?: boolean;
    readonly failedMessage?: string;
    readonly onRetry?: () => void;
    readonly placeholder?: string;
    readonly searchPlaceholder?: string;
    readonly emptyMessage?: ReactNode;
    readonly onValueChange?: (value: string | null, option: ComboboxOption | null) => void;
  },
) {
  const { t } = useLingui();
  const { setValue, control: formControl } = useFormContext<V>();
  const display = useWatch({ control: formControl, name: props.displayName ?? props.name }) as unknown;
  const emptyValue = props.emptyValue === undefined ? '' : props.emptyValue;
  const { className, ...bound } = props;
  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      <Bound props={bound}>
        {(control, { field }) => {
          const value = typeof field.value === 'string' && field.value !== '' ? field.value : null;
          const label = props.displayName && typeof display === 'string' && display !== '' ? display : undefined;
          return (
            <Combobox
              {...control}
              options={props.options}
              value={value}
              {...(label ? { selectedLabel: label } : {})}
              loading={props.loading === true}
              disabled={control.disabled}
              readOnly={control.readOnly}
              {...(props.placeholder ? { placeholder: props.placeholder } : {})}
              {...(props.searchPlaceholder ? { searchPlaceholder: props.searchPlaceholder } : {})}
              emptyMessage={props.loading === true ? t`Carregando…` : (props.emptyMessage ?? t`Nenhuma opção encontrada.`)}
              onChange={(next, option) => {
                field.onChange(next ?? emptyValue);
                if (props.displayName) setValue(props.displayName, (option?.label ?? emptyValue) as PathValue<V, Path<V>>, { shouldDirty: true });
                field.onBlur();
                props.onValueChange?.(next, option);
              }}
            />
          );
        }}
      </Bound>
      {props.failed ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-err">
          <span>{props.failedMessage ?? t`Não foi possível carregar as opções.`}</span>
          {props.onRetry ? (
            <Button type="button" variant="secondary" size="sm" onClick={props.onRetry}>
              {t`Tentar de novo`}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Número do navegador; mantém o texto digitado, inclusive vazio, como o legado. */
export function NumberFormField<V extends FieldValues>(props: BoundFieldProps<V>) {
  return <TextFormField<V> {...props} type="number" />;
}
/** Hora HH:mm; mantém o texto e o foco/blur do campo original. */
export function TimeFormField<V extends FieldValues>(props: BoundFieldProps<V>) {
  return <Bound<V> props={props}>{(control, { field }) => <Input {...control} ref={field.ref} type="time" value={typeof field.value === 'string' ? field.value : ''} onChange={(event) => field.onChange(event.target.value)} onBlur={field.onBlur} />}</Bound>;
}
